/**
 * String Primitives
 *
 * Reusable string validation schemas for common patterns.
 */
import { z } from "zod";

/** Email validation with custom message */
export const emailSchema = z
	.string()
	.email("Please enter a valid email address");
export type Email = z.infer<typeof emailSchema>;

/** Slug validation (lowercase, alphanumeric, hyphens) */
export const slugSchema = z
	.string()
	.min(1)
	.max(100)
	.regex(
		/^[a-z0-9]+(?:-[a-z0-9]+)*$/,
		"Slug must be lowercase and may only contain letters, numbers, and hyphens",
	);
export type Slug = z.infer<typeof slugSchema>;

/** Title/name validation */
export const titleSchema = z.string().min(1).max(200);
export type Title = z.infer<typeof titleSchema>;

/** Meta description validation */
export const metaDescriptionSchema = z.string().max(300);
export type MetaDescription = z.infer<typeof metaDescriptionSchema>;

/**
 * Bahrain phone number validation regex
 * Accepts:
 * - +973 XXXX XXXX (with optional spaces)
 * - +973XXXXXXXX (no spaces)
 * - XXXX XXXX (local 8-digit with space)
 * - XXXXXXXX (local 8-digit no space)
 */
export const bahrainPhoneRegex = /^(\+973\s?)?\d{4}\s?\d{4}$/;

/** Bahrain phone validation schema */
export const bahrainPhoneSchema = z
	.string()
	.regex(bahrainPhoneRegex, "Please enter a valid Bahrain phone number");
export type BahrainPhone = z.infer<typeof bahrainPhoneSchema>;

/** Generic search string with max length */
export const searchSchema = z.string().max(100);
export type Search = z.infer<typeof searchSchema>;

/** Text content with minimum length */
export const textContentSchema = (minLength: number, message: string) =>
	z.string().min(minLength, message);
